import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { BiLike, BiSolidLike } from "react-icons/bi";
import { CiLinkedin } from "react-icons/ci";
import { HiOutlineChatBubbleBottomCenter } from "react-icons/hi2";
import { LiaYoutube } from "react-icons/lia";
import { MdKeyboardBackspace } from "react-icons/md";
import { Link } from "react-router-dom";

import firebase, { database } from "../config";
import { useAppContext } from "../context/ThemeContext";
import { Comments } from "../reusable-components/custom-component/Comments";
import CustomImage from "../reusable-components/Image";
import Layout from "../reusable-components/Layout";
import PlaceholderBar from "../reusable-components/PlaceholderBar";
import { extractClientAndDeliverableFromUrl } from "../utilities/extractClientAndDeliverabledFromUrl";
import { incrementViewCount } from "../utilities/firebase-connection";

const LinkNav = ({ isDay }) => (
  <Link
    to="/nexus/blog"
    className={`flex flex-row items-center w-fit gap-1 pl-2 pr-3 py-[2px] ease-out duration-300 cursor-pointer rounded-full text-xs ${
      isDay
        ? "bg-black text-white hover:bg-black/80"
        : "bg-white text-black hover:bg-white/80"
    }`}
  >
    <MdKeyboardBackspace size={16} />
    Back to Blog
  </Link>
);

function Post() {
  const { siteTheme, state } = useAppContext();
  const [post, setPost] = useState({});
  const [comments, setComments] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const pathname = window.location.pathname;

  const { client: postid } = extractClientAndDeliverableFromUrl(
    pathname,
    "/nexus/blog/"
  );

  let isDay = state.isDay;
  if (JSON.parse(sessionStorage.getItem("isDay"))) {
    isDay = JSON.parse(sessionStorage.getItem("isDay"));
  }

  useEffect(() => {
    let loadId;

    const unsubscribePost = database
      .collection("posts")
      .doc(postid)
      .onSnapshot((doc) => {
        if (doc.exists) {
          setPost(doc.data());
        }
      });

    const unsubscribeComments = database
      .collection("post-comments")
      .doc(postid)
      .collection("comments")
      .orderBy("timestamp", "asc")
      .onSnapshot((snapshot) => {
        const comments = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setComments(comments);
      });

    setLoading(true);
    loadId = setTimeout(() => {
      setLoading(false);
      incrementViewCount(postid);
    }, 2000);

    // Return cleanup function
    return () => {
      clearTimeout(loadId);
      unsubscribePost();
      unsubscribeComments();
    };
  }, [postid]);

  const socialIcon = {
    linkedin: <CiLinkedin size={20} />,
    youtube: <LiaYoutube size={23} />,
  };

  function isPostLiked(postId) {
    const liked = JSON.parse(localStorage.getItem("likedPosts")) || [];
    return liked.includes(postId);
  }

  // Like a post and save it locally
  function handleLike(postId) {
    const liked = JSON.parse(localStorage.getItem("likedPosts")) || [];

    if (!liked.includes(postId)) {
      // Update Firestore
      database
        .collection("posts")
        .doc(postId)
        .update({
          likes: firebase.firestore.FieldValue.increment(1),
        });

      // Update localStorage
      const updated = [...liked, postId];
      localStorage.setItem("likedPosts", JSON.stringify(updated));
    }
  }

  // Optional: Unlike a post
  function handleUnlike(postId) {
    const liked = JSON.parse(localStorage.getItem("likedPosts")) || [];

    if (liked.includes(postId)) {
      // Decrease Firestore count
      database
        .collection("posts")
        .doc(postId)
        .update({
          likes: firebase.firestore.FieldValue.increment(-1),
        });

      // Update localStorage
      const updated = liked.filter((id) => id !== postId);
      localStorage.setItem("likedPosts", JSON.stringify(updated));
    }
  }

  return (
    <>
      <Helmet>
        <title>Wiingr | Blog</title>
      </Helmet>
      <Layout>
        <div
          className={`${siteTheme.textColor} relative ease-in duration-300 flex flex-col w-full h-[100vh] sm:px-4 mx-auto max-w-max-content-width overflow-hidden no-scrollbar`}
        >
          {loading ? (
            <div className="flex flex-col gap-1 mt-24 w-full sm:w-[70%] mx-auto px-5">
              <PlaceholderBar classname="w-[60%] h-[30px] placeholder" />
              <PlaceholderBar classname="w-[40%] h-[30px] placeholder" />
              <PlaceholderBar classname="w-[15%] h-[10px] mb-10 placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] mb-10 placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] mb-10 placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] mb-10 placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] mb-10 placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
              <PlaceholderBar classname="w-full h-[10px] placeholder" />
            </div>
          ) : Object.values(post)?.length === 0 ? (
            <div className="mx-auto mt-24 font-thin tracking-wider w-fit font-saira">
              <p
                className={`text-center px-4 py-1 flex flex-col items-center gap-2 rounded-lg ${siteTheme.toolText} ${siteTheme.toolBackground}`}
              >
                <>
                  <span>
                    Oops! No post found. It might have been removed or the ID is
                    incorrect.
                  </span>
                  <code> --{postid}--</code>
                </>
                <LinkNav isDay={isDay} />
              </p>
            </div>
          ) : (
            <>
              <div className="relative w-full gap-5 font-thin font-saira max-h-1/3 h-1/3 lg:max-1/2 lg:h-1/2">
                <CustomImage
                  src={post?.post?.image}
                  alt="Post"
                  className="object-cover w-full h-full"
                />
                <div
                  className={`ease-in duration-300 absolute inset-0 bg-gradient-to-b ${
                    isDay ? "from-white/80" : "from-black/80"
                  } ${
                    isDay ? "to-brandWhiteBackground" : "to-brandDarkBackground"
                  }`}
                ></div>
              </div>
              <div className="w-[90%] lg:w-[70%] mx-auto -mt-[35%] xs:-mt-[10%] lg:-mt-[20%] z-10 font-saira">
                <div className="flex flex-row items-center gap-3">
                  <LinkNav isDay={isDay} />
                  <div className="flex flex-row items-center gap-3 sm:mr-3">
                    {post?.post?.socials.map((social, idx) => (
                      <Link to={`${social?.url}`} target="_blank" key={idx}>
                        {socialIcon[social?.socialName]}
                      </Link>
                    ))}
                  </div>
                </div>
                <h1
                  style={{ textShadow: "0 0px 10px rgba(0, 0, 0, 0.2)" }}
                  className="mt-4 mb-1 w-[90%] text-base sm:text-2xl xl:text-4xl leading-6 sm:leading-tight font-saira"
                >
                  {post?.post?.title}
                </h1>
                <div className="pb-20">
                  <div className="flex flex-row items-center justify-end gap-1 mb-3">
                    <div
                      className={`border-r-[0.5px] ${siteTheme.borderColor} pr-1`}
                    >
                      <span className="text-xs">
                        {post?.views} {post?.views > 1 ? "views" : "view"}
                      </span>
                    </div>
                    {comments?.length ? (
                      <div
                        className={`border-r-[0.5px] text-teal ${siteTheme.borderColor} pr-1`}
                      >
                        <span className="text-xs">
                          {comments?.length}{" "}
                          {comments?.length > 1 ? "comments" : "comment"}
                        </span>
                      </div>
                    ) : (
                      <></>
                    )}
                    <div
                      onClick={() =>
                        isPostLiked(postid)
                          ? handleUnlike(postid)
                          : handleLike(postid)
                      }
                      className="flex flex-row items-center gap-1 text-xs duration-300 ease-in cursor-pointer hover:text-orange-600"
                    >
                      {isPostLiked(postid) ? (
                        <BiSolidLike size={14} />
                      ) : (
                        <BiLike size={14} />
                      )}{" "}
                      {post?.likes ? post?.likes : ""}{" "}
                      {post?.likes > 1 ? "likes" : "like"}
                    </div>
                  </div>
                  <div
                    dangerouslySetInnerHTML={{ __html: post?.post?.paragraph }}
                  />
                  <div className="fixed right-4 bottom-20 sm:right-[20%] flex gap-3">
                    <div className="cursor-pointer">
                      <div
                        onClick={() =>
                          isPostLiked(postid)
                            ? handleUnlike(postid)
                            : handleLike(postid)
                        }
                        className={[
                          "flex items-center justify-center text-xl rounded-full w-6 h-6 p-1",
                          siteTheme.headingBackground,
                        ].join(" ")}
                      >
                        {isPostLiked(postid) ? (
                          <BiSolidLike size={24} />
                        ) : (
                          <BiLike size={24} />
                        )}
                      </div>
                    </div>
                    <div className="hidden cursor-pointer sm:inline-block">
                      <div
                        onClick={() => setShowComment(true)}
                        className={[
                          "flex items-center justify-center text-xl rounded-full w-6 h-6 p-1",
                          siteTheme.headingBackground,
                        ].join(" ")}
                      >
                        <HiOutlineChatBubbleBottomCenter size={24} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Comments
                postid={postid}
                comments={comments}
                setShowComment={setShowComment}
                showComment={showComment}
              />
            </>
          )}
        </div>
      </Layout>
    </>
  );
}

export default Post;
